import axios from 'axios';
import clientConfig from '../config/client_config.js';
import Cookies from 'js-cookie';

const LJ_TOKEN_KEY = 'lk_token'

// 【G18 增强】飞书 H5 SDK 1.5.23 不允许重复调 h5sdk.config，
// 重复调可能报错（"signature is expired" / "config:fail"）。
// 这里用模块级标记记录首次鉴权是否成功，后续直接复用。
let __JSAPI_AUTHORIZED__ = false;

/**
 * 检测 errCode 105 / errMsg 含 'invalid session' → session 失效
 */
function isInvalidSessionError(err) {
    if (!err) return false;
    return err.errCode === 105 || (typeof err.errMsg === 'string' && err.errMsg.includes('invalid session'));
}

/**
 * 遇到 invalid session 时重置鉴权标记并重新鉴权，然后重试 JSAPI 调用
 * @param {Function} jsApiCall - 执行 JSAPI 调用的函数（可异步）
 * @param {number} maxRetries - 最大重试次数（默认 1）
 * @returns {Promise<any>} JSAPI 调用的结果
 */
export function retryJSAPIOnInvalidSession(jsApiCall, maxRetries = 1) {
    return (async () => {
        try {
            return await jsApiCall();
        } catch (err) {
            if (isInvalidSessionError(err) && maxRetries > 0) {
                console.warn('[JSAPI] invalid session，强制重新鉴权后重试:', err.errMsg || err);
                __JSAPI_AUTHORIZED__ = false;
                // 重新鉴权
                await new Promise((resolve) => {
                    handleJSAPIAccess((ok) => resolve(ok));
                });
                // 重试一次
                return retryJSAPIOnInvalidSession(jsApiCall, maxRetries - 1)();
            }
            throw err;
        }
    })();
}

/// ---------------- JSAPI鉴权 部分 -------------------------

export async function handleJSAPIAccess(complete) {

    // 【G18】已鉴权过：直接成功返回，不再调 h5sdk.config
    if (__JSAPI_AUTHORIZED__) {
        console.log("[G18] JSAPI 已鉴权过，跳过重复 config");
        complete(true);
        return;
    }

    console.log("\n----------[接入方网页JSAPI鉴权处理 BEGIN]----------")
    const url = encodeURIComponent(window.location.href.split("#")[0]);
    console.log("接入方前端[JSAPI鉴权处理]第① 步: 请求JSAPI鉴权参数")
    // 向接入方服务端发起请求，获取鉴权参数（appId、timestamp、nonceStr、signature）
    // 带 tenantKey：Node session 重启清空后可从预热 cache 兜底取租户配置
    try {
        const tk = localStorage.getItem('tenantKey') || ''
        const res = await axios.get(`${getOrigin(clientConfig.apiPort)}${clientConfig.getSignParametersPath}?url=${url}${tk ? `&tenantKey=${encodeURIComponent(tk)}` : ''}`,
            { withCredentials: true }
        )
        if (!res.data) {
            console.error(`${clientConfig.getSignParametersPath} fail`)
            complete(false)
            return
        }

        const data = res.data.data
        console.log("接入方前端[JSAPI鉴权处理]第② 步: 获得鉴权参数")
        if (!data) {
            console.error('获取参数失败')
            complete(false)
            return
        }
        console.log("接入方前端[JSAPI鉴权处理]第③ 步: 通过window.h5sdk.config进行鉴权")
        configJSAPIAccess(data, complete)
    } catch (error) {
        console.error('[JSAPI鉴权] 请求鉴权参数失败，继续加载应用:', error.message || error)
        complete(false)
    }
}

//config JSAPI鉴权
function configJSAPIAccess(data, complete) {
    // 【G18】已鉴权过直接成功返回
    if (__JSAPI_AUTHORIZED__) {
        complete(true);
        return;
    }

    // 检查window.h5sdk是否存在
    if (!window.h5sdk) {
        console.log('非飞书环境，跳过JSAPI鉴权')
        complete(false)
        return
    }

    //配置要使用的jsapi列表
    let jsApiList = [
        "tt.getSystemInfo",
        "tt.showActionSheet",
        "tt.previewImage",
        "tt.showToast",
        "tt.chooseContact",
        "tt.scanCode",
        "tt.chooseImage",
        "tt.uploadFile",
        "tt.compressImage",
        "tt.getImageInfo",
        "tt.enterChat"
    ]

    // 调用config接口进行鉴权
    // 传完整签名参数（文档要求 chooseContact 必须在鉴权后调用）
    window.h5sdk.config({
        appId: data.app_id,
        timestamp: data.timestamp,
        nonceStr: data.noncestr,
        signature: data.signature,
        jsApiList: jsApiList,
        //成功回调
        onSuccess: (res) => {
            console.log(`鉴权成功: ${JSON.stringify(res)}`);
            __JSAPI_AUTHORIZED__ = true;  // 【G18】标记已鉴权
            complete(true)
            console.log("\n----------[接入方网页鉴权处理 END]----------")
        },
        //失败回调
        onFail: (err) => {
            console.warn(`鉴权失败(尝试去掉末尾斜杠): ${JSON.stringify(err)}`);
            complete(false)
        },
    });
}

/// ---------------- 应用免登 部分 -------------------------
//处理用户免登逻辑
export async function handleUserAuth(complete) {

    console.log("\n----------[接入方网页免登处理 BEGIN]----------")
    let lj_tokenString = Cookies.get(LJ_TOKEN_KEY) || ""
    if (lj_tokenString.length > 0) {
        console.log("接入方前端[免登处理]第① 步: 用户已登录，请求后端验证...")
        requestUserAccessToken("", complete)
    } else {
        // 更灵活的环境检查：支持飞书开发者工具环境
        const isFeishuEnv = window.h5sdk || window.feishu || window.lark || window.tt;
        if (!isFeishuEnv) {
            console.log('非飞书环境，无法获取用户信息')
            complete()
            return
        }
        console.log("接入方前端[免登处理]第① 步: 依据App ID调用JSAPI tt.requestAuthCode 请求免登授权码")

        // 【G18 修复】飞书 SDK 1.5.23 强制要求 h5sdk.config 必须在 h5sdk.ready 之前调用，
        // 否则后续 JSAPI 调用会报 "signature is expired" / "config:fail"。
        // 这里先做一次最小 config（不带 signature），用于初始化 JSAPI 白名单，
        // 待 handleJSAPIAccess 拿到真实 signature 后再覆盖一次。
        const ensureSDKConfigured = () => {
            return new Promise((resolve) => {
                if (window.h5sdk && typeof window.h5sdk.config === 'function') {
                    try {
                        window.h5sdk.config({
                            appId: clientConfig.appId,
                            jsApiList: [
                                "tt.getSystemInfo",
                                "tt.showActionSheet",
                                "tt.previewImage",
                                "tt.showToast",
                                "tt.chooseContact",
                                "tt.chooseImage",
                                "tt.uploadFile",
                                "tt.compressImage",
                                "tt.getImageInfo",
                                "tt.enterChat"
                            ]
                        });
                        console.log("免登前置h5sdk.config已完成（最小参数）");
                    } catch (cfgErr) {
                        console.warn("免登前置h5sdk.config异常（继续）:", cfgErr);
                    }
                    resolve();
                } else {
                    // 等待 h5sdk 加载完成
                    const interval = setInterval(() => {
                        if (window.h5sdk && typeof window.h5sdk.config === 'function') {
                            clearInterval(interval);
                            try {
                                window.h5sdk.config({
                                    appId: clientConfig.appId,
                                    jsApiList: [
                                        "tt.getSystemInfo",
                                        "tt.showActionSheet",
                                        "tt.previewImage",
                                        "tt.showToast",
                                        "tt.chooseContact",
                                        "tt.chooseImage",
                                        "tt.uploadFile",
                                        "tt.compressImage",
                                        "tt.getImageInfo",
                                        "tt.enterChat"
                                    ]
                                });
                                console.log("免登前置h5sdk.config已完成（最小参数）");
                            } catch (cfgErr) {
                                console.warn("免登前置h5sdk.config异常（继续）:", cfgErr);
                            }
                            resolve();
                        }
                    }, 100);
                    setTimeout(() => { clearInterval(interval); resolve(); }, 3000);
                }
            });
        };

        // 确保SDK加载完成后再调用API
        const checkSDKReady = () => {
            return new Promise((resolve) => {
                // 如果SDK已经加载完成，直接调用
                if (window.h5sdk && window.h5sdk.ready) {
                    window.h5sdk.ready(resolve);
                } else if (window.tt && window.tt.requestAuthCode) {
                    // 飞书开发者工具可能直接暴露tt对象
                    resolve();
                } else {
                    // 等待SDK加载完成
                    const interval = setInterval(() => {
                        if (window.h5sdk && window.h5sdk.ready) {
                            clearInterval(interval);
                            window.h5sdk.ready(resolve);
                        } else if (window.tt && window.tt.requestAuthCode) {
                            clearInterval(interval);
                            resolve();
                        }
                    }, 100);
                    // 最多等待3秒
                    setTimeout(() => clearInterval(interval), 3000);
                }
            });
        };

        try {
            // 【G18】先 config 再 ready（顺序至关重要）
            await ensureSDKConfigured();
            await checkSDKReady();
            console.log("SDK已准备就绪");

            // 调用requestAuthCode获取授权码
            window.tt.requestAuthCode({
                appId: clientConfig.appId,
                success: (info) => {
                    const code = info.code
                    if (code.length <= 0) {
                        console.error('auth code为空')
                        complete()
                    } else {
                        requestUserAccessToken(code, complete)
                    }
                },
                fail: (error) => {
                    console.error("window.tt.requestAuthCode", error)
                    // 即使失败，也尝试继续执行，获取基本的用户信息
                    requestUserAccessToken("", complete);
                }
            });
        } catch (error) {
            console.error("SDK准备失败:", error)
            // 出错时也尝试继续执行，获取基本的用户信息
            requestUserAccessToken("", complete);
        }
    }
}

function requestUserAccessToken(code, complete) {

    // 获取user_access_token信息
    console.log("接入方前端[免登处理]第② 步: 去接入方服务端获取user_access_token信息")
    axios.get(`${getOrigin(clientConfig.apiPort)}${clientConfig.getUserAccessTokenPath}?code=${code}`,
        { withCredentials: true }   //调用时设置 请求带上cookie
    ).then(function (response) {  // ignore_security_alert
        if (!response.data) {
            console.error(`${clientConfig.getUserAccessTokenPath} response is null`)
            complete()
            return
        }
        const data = response.data.data
        if (data) {
            console.log("接入方前端[免登处理]第③ 步: 获取user_access_token信息")
            complete(data)
            localStorage.setItem(LJ_TOKEN_KEY, data.access_token)
            console.log("----------[接入网页方免登处理 END]----------\n")
        } else {
            console.error("接入方前端[免登处理]第③ 步: 未获取user_access_token信息")
            complete()
            console.log("----------[接入网页方免登处理 END]----------\n")
        }
    }).catch(function (error) {
        console.log(`${clientConfig.getSignParametersPath} error:`, error)
        complete()
        console.log("----------[接入网页方免登处理 END]----------\n")
    })
}

export function getOrigin(apiPort) {
    // 生成完整URL，根据环境判断是否需要添加端口号
    let hostname = window.location.hostname;
    let protocol = window.location.protocol;
    
    // 检查是否为生产环境：如果是https，则不添加端口号
    // 开发环境中（包括所有IP地址）都添加端口号
    if (protocol === 'https:') {
        return `${protocol}//${hostname}`;
    }
    
    // 开发环境中添加端口号
    return `${protocol}//${hostname}:${apiPort}`;
}

/// ---------------- 用户缓存管理 部分 -------------------------

// 检查缓存是否过期
export function isCacheExpired(key, maxAge = 24 * 60 * 60 * 1000) {
    const item = localStorage.getItem(key);
    if (!item) return true;
    
    try {
        const parsed = JSON.parse(item);
        const now = new Date().getTime();
        return now - parsed.timestamp > maxAge;
    } catch (error) {
        console.error('解析缓存数据失败:', error);
        return true;
    }
}

// 为特定用户设置带过期时间的缓存
export function setUserCachedData(userId, key, data, maxAge = 24 * 60 * 60 * 1000) {
    const userKey = `${key}_${userId}`;
    const item = {
        data: data,
        timestamp: new Date().getTime()
    };
    localStorage.setItem(userKey, JSON.stringify(item));
}

// 为特定用户获取缓存数据
export function getUserCachedData(userId, key) {
    const userKey = `${key}_${userId}`;
    const item = localStorage.getItem(userKey);
    if (!item) return null;
    
    try {
        const parsed = JSON.parse(item);
        return parsed.data;
    } catch (error) {
        console.error('解析缓存数据失败:', error);
        return null;
    }
}

// 为特定用户清除缓存
export function clearUserCachedData(userId, key) {
    const userKey = `${key}_${userId}`;
    localStorage.removeItem(userKey);
}

// 获取当前用户的缓存键
export function getUserCacheKey(userId, key) {
    return `${key}_${userId}`;
}

// 清除所有用户的缓存
export function clearAllUserCachedData(key) {
    // 遍历localStorage，清除所有以指定key开头的缓存
    for (let i = 0; i < localStorage.length; i++) {
        const storageKey = localStorage.key(i);
        if (storageKey && storageKey.startsWith(`${key}_`)) {
            localStorage.removeItem(storageKey);
        }
    }
}